// Календарь — встречи и события компании из зеркала календаря Bitrix. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТОТ ЭКРАН ОТВЕЧАЕТ. «Кто когда занят и чем» — по дню и по неделе, по всем людям
// сразу или по одному. Это календарь ВСТРЕЧ (bxc_*), а не «Календарь инженеров»: тот
// строится из сделок и отвечает «кто на каком судне», этот — из календаря Bitrix и
// отвечает «у кого какая встреча».
//
// СТРОКА ЗДЕСЬ — ВХОЖДЕНИЕ, А НЕ СОБЫТИЕ. Ядро разворачивает повторяющиеся встречи по
// правилу повтора: еженедельная пятиминутка в недельном окне — это несколько строк
// одного события, и так и надо. Схлопнуть их «для красоты» значило бы показать занятого
// человека свободным во все дни, кроме первого.
//
// МУСОРНЫЕ ИМПОРТЫ ИСТОЧНИКА. В зеркале лежат разовые свалки — десятки тысяч одинаковых
// событий, сваленных в один день прошлого. Правило владельца: история как есть, оговорки
// можно, исправления нет. Поэтому экран ничего не прячет: по умолчанию открыт день/неделя,
// куда свалки не попадают сами собой, оговорка с числами приезжает из ядра (посчитана
// запросом, не вбита), а день свалки ядро обрезает пределом и говорит об этом флагом.
//
// ЧАСТНЫЕ СОБЫТИЯ. Время и занятость показываются, текст — нет: его не переносили из
// Bitrix вовсе. Строка «Занято» — это не пустота, а ответ.
//
// ОТПУСКОВ ЗДЕСЬ НЕТ, И ССЫЛКА НА НИХ ЕСТЬ. Отсутствия — другой слой (график Bitrix),
// ключа связи между ним и календарём в данных нет — это измерено, и связь не рисуется
// по догадке. Экран говорит об этом словами и ведёт в раздел «Отсутствия» ссылкой.
//
// ЧИСЕЛ-ЗАМЕРОВ в этой шапке нет намеренно: всё, что про объём данных, экран печатает
// из ответа ядра.
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, pluralN } from '../lib/format'

// ─────────────────────────── что отдаёт ядро ───────────────────────────

interface Вхождение {
  event_id: number
  parent_id: number | null
  starts_at: string
  ends_at: string | null
  all_day: boolean
  accessibility: string | null
  /** null у частного события: текст не переносился вовсе. */
  name: string | null
  location: string | null
  text_hidden: boolean
  is_private: boolean
  owner_id: number
  owner_name: string
  section_id: number | null
  section_name: string | null
  section_color: string | null
  is_meeting: boolean
  recurring: boolean
  attendees: number
  declined: number
}

interface День {
  date: string
  wd: string
  day: number
  month: number
  weekend: boolean
  today: boolean
}

interface Окно {
  from: string
  to: string
  days_count: number
  days: День[]
  today: string
}

interface ЧеловекОкна {
  owner_id: number
  owner_name: string
  entries: number
  events: number
  hidden: number
}

interface ОтветОкна {
  window: Окно
  tz: string
  rows: Вхождение[]
  total: number
  trimmed: boolean
  limit: number
  people: ЧеловекОкна[]
  counts: {
    entries: number
    events: number
    meetings: number
    people: number
    hidden: number
    all_day: number
  }
  notes: Record<string, string>
}

interface Владелец {
  owner_id: number
  name: string
  active: boolean | null
  entries: number
}

interface Свалка {
  name: string
  events: number
  day: string
}

interface Справочники {
  tz: string
  counts: Record<string, number | string | null>
  dumps: Свалка[]
  sections: Array<{ id: number; name: string; owner_name: string; events: number }>
  owners: Владелец[]
  freshness: Array<{ entity: string; last_sync_at: string | null; age_min: number | null }>
  notes: Record<string, string>
}

// ─────────────────────────── транспорт ───────────────────────────
// Свой fetch, а не общий api/client: там эмуляция Bitrix REST (POST /api/bx с method),
// здесь обычная GET-ручка ядра. Разбор тот же, что у календаря инженеров: 401 уводит на
// вход, 400 поднимается отдельным типом, чтобы QueryState напечатал «такой фильтр
// обслужить нельзя» вместо пустого календаря — пустой календарь читается как «все
// свободны», и это самая дорогая ложь раздела.

const БАЗА = '/api/calendar'

async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────────────────────── время на экране ───────────────────────────
// Пояс приезжает из ядра и подставляется в РАЗБОР, а не подразумевается: время без
// названного пояса читалось бы как местное время смотрящего, и у сотрудника во
// Владивостоке встреча уехала бы на семь часов.

function времяВПоясе(iso: string, пояс: string): string {
  try {
    return new Date(iso).toLocaleTimeString('ru-RU',
      { timeZone: пояс, hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso.slice(11, 16)
  }
}

function деньВПоясе(iso: string, пояс: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-CA', { timeZone: пояс })
  } catch {
    return iso.slice(0, 10)
  }
}

/** Подпись строки: у частного события текста нет, и «Занято» — ответ, а не пустота. */
function подпись(в: Вхождение): string {
  if (в.text_hidden || в.name === null) return 'Занято'
  return в.name || 'Без названия'
}

/** Попадает ли вхождение в день окна (пересечением, как в ядре). */
function вДне(в: Вхождение, деньISO: string, пояс: string): boolean {
  const нач = деньВПоясе(в.starts_at, пояс)
  const кон = деньВПоясе(в.ends_at ?? в.starts_at, пояс)
  return нач <= деньISO && деньISO <= кон
}

function сдвигДня(деньISO: string, дней: number): string {
  const [г, м, д] = деньISO.split('-').map(Number)
  const дата = new Date(Date.UTC(г, м - 1, д + дней))
  return дата.toISOString().slice(0, 10)
}

// ═══════════════════════════════ экран ═══════════════════════════════

export default function Calendar() {
  const [режим, setРежим] = useState<'day' | 'week'>('day')
  /** Пусто — «сегодня/эта неделя» решает ядро по поясу компании, а не браузер. */
  const [дата, setДата] = useState('')
  const [человек, setЧеловек] = useState('')

  const справочники = useQuery({
    queryKey: ['calendar', 'refs'],
    queryFn: () => запрос<Справочники>('/refs'),
    retry: 1,
    staleTime: 60_000,
  })

  const параметры = useMemo(() => {
    const п = new URLSearchParams()
    if (дата) п.set(режим === 'day' ? 'date' : 'from', дата)
    if (человек) п.set('user', человек)
    const s = п.toString()
    return s ? `?${s}` : ''
  }, [режим, дата, человек])

  const окно = useQuery({
    queryKey: ['calendar', режим, параметры],
    queryFn: () => запрос<ОтветОкна>(`/${режим}${параметры}`),
    placeholderData: (prev) => prev,
    retry: 1,
    staleTime: 60_000,
  })

  const д = окно.data
  const сп = справочники.data
  const пояс = д?.tz ?? сп?.tz ?? 'Europe/Moscow'
  const оговорки = { ...(сп?.notes ?? {}), ...(д?.notes ?? {}) }
  const зеркало = сп?.freshness.find((з) => з.entity === 'bxc:events')

  /** Листание на шаг окна. От границы текущего ответа, а не от «сегодня браузера». */
  const шагнуть = (куда: -1 | 1) => {
    if (!д) return
    setДата(сдвигДня(д.window.from, куда * д.window.days_count))
  }

  return (
    <>
      <div className="page-head">
        <h1>Календарь</h1>
        <span className="spacer" />
        <span className="hint">
          {д
            ? `${fmtDate(д.window.from)} — ${fmtDate(д.window.to)} · `
              + pluralN(д.total, 'запись', 'записи', 'записей')
            : ''}
          {зеркало?.last_sync_at
            ? ` · зеркало от ${fmtDate(зеркало.last_sync_at, true)}`
            : ''}
        </span>
      </div>

      {оговорки.dumps && (
        <div className="notice">
          <b>В зеркале есть мусорные импорты источника, и они не вычищены.</b>{' '}
          {оговорки.dumps}
        </div>
      )}
      <div className="notice">
        {оговорки.absence ?? 'Отпуска, больничные и командировки календарь не показывает: '
          + 'это другой слой, и ключа связи между ними в данных нет.'}{' '}
        Смотреть их — в разделе <Link to="/absence">«Отсутствия»</Link>.
      </div>

      <QueryState isLoading={окно.isLoading} error={окно.error ?? справочники.error}>
        {д && (
          <>
            <Плитки д={д} />

            <div className="card" style={{ marginBottom: 16 }}>
              <FilterBar id="calendar">
                <div className="f">
                  <label htmlFor="calv">Окно</label>
                  <select id="calv" value={режим}
                          onChange={(e) => setРежим(e.target.value === 'week' ? 'week' : 'day')}>
                    <option value="day">день</option>
                    <option value="week">неделя</option>
                  </select>
                </div>

                <div className="f">
                  <label htmlFor="cald">
                    {режим === 'day' ? 'Дата' : 'Неделя с даты'}
                  </label>
                  <input id="cald" type="date" value={дата}
                         onChange={(e) => setДата(e.target.value)} />
                </div>

                <div className="f">
                  <label htmlFor="calu">Человек</label>
                  <select id="calu" value={человек}
                          onChange={(e) => setЧеловек(e.target.value)}>
                    <option value="">все</option>
                    {(сп?.owners ?? []).map((в) => (
                      <option key={в.owner_id} value={String(в.owner_id)}>
                        {в.name || `Учётка №${в.owner_id}`}
                      </option>
                    ))}
                  </select>
                </div>
              </FilterBar>

              {/* flexWrap: на 320px три кнопки листания в одну строку не помещаются —
                  пусть переносятся, а не выталкивают страницу вбок. */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                <button type="button" onClick={() => шагнуть(-1)}>
                  ← Раньше
                </button>
                <button type="button" onClick={() => setДата('')}>
                  {режим === 'day' ? 'Сегодня' : 'Эта неделя'}
                </button>
                <button type="button" onClick={() => шагнуть(1)}>
                  Позже →
                </button>
              </div>
            </div>

            {д.trimmed && (
              <div className="notice">
                Показаны не все строки: в этом окне{' '}
                {pluralN(д.total, 'запись', 'записи', 'записей')}, ответ обрезан пределом{' '}
                {pluralN(д.limit, 'строки', 'строк', 'строк')}. Такое бывает на днях
                мусорных импортов — сузьте окно до дня или выберите человека, иначе
                календарь врёт полнотой.
              </div>
            )}

            {режим === 'day'
              ? <ТаблицаДня д={д} пояс={пояс} />
              : <СеткаНедели д={д} пояс={пояс} выбрать={(id) => setЧеловек(String(id))} />}

            <Границы сп={сп} оговорки={оговорки} />
          </>
        )}
      </QueryState>
    </>
  )
}

// ═══════════════════════════════ плитки ═══════════════════════════════

function Плитки({ д }: { д: ОтветОкна }) {
  const плитки: Array<[string, number, string]> = [
    ['Записей в окне', д.counts.entries,
      'вхождений календаря: повторяющаяся встреча считается на каждую свою дату'],
    ['Людей', д.counts.people, 'у скольких людей есть хотя бы одна запись в окне'],
    ['Встреч', д.counts.meetings,
      'встреч с участниками, посчитано по организатору — копии участников не удваивают счёт'],
    ['Частных', д.counts.hidden,
      'записи, у которых видны только время и занятость: текст не переносился вовсе'],
  ]
  return (
    <div className="tiles" style={{ marginBottom: 16 }}>
      {плитки.map(([имя, число, смысл]) => (
        <div key={имя} className="tile" title={смысл}>
          <div className="hint">{имя}</div>
          <div className="t-sum">{число}</div>
        </div>
      ))}
    </div>
  )
}

// ═══════════════════════════════ день ═══════════════════════════════

function ТаблицаДня({ д, пояс }: { д: ОтветОкна; пояс: string }) {
  if (д.rows.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="empty">
          <div className="empty-title">В этот день нет ни одной записи календаря</div>
          <div className="empty-hint">
            Это ответ про выбранный день и фильтр, а не про весь календарь: смените дату
            или снимите выбор человека. Частные события экран не прячет — их здесь тоже нет.
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Время</th>
              <th>Событие</th>
              <th>Чей календарь</th>
              <th>Раздел</th>
              <th className="num">Участников</th>
            </tr>
          </thead>
          <tbody>
            {д.rows.map((в, i) => (
              <tr key={`${в.event_id}-${в.starts_at}-${i}`}>
                <td data-label="Время">
                  {в.all_day
                    ? 'весь день'
                    : `${времяВПоясе(в.starts_at, пояс)}${в.ends_at
                        ? `–${времяВПоясе(в.ends_at, пояс)}` : ''}`}
                </td>
                <td data-label="Событие">
                  {в.text_hidden
                    ? <span className="badge" title="Частное событие: текст не переносился из Bitrix вовсе">Занято</span>
                    : <>
                        {подпись(в)}
                        {в.recurring && (
                          <span className="hint" title="Повторяющееся событие: это одно из его вхождений">
                            {' '}↻
                          </span>
                        )}
                        {в.location && <div className="hint">{в.location}</div>}
                      </>}
                </td>
                <td data-label="Чей календарь">
                  {в.owner_name || `Учётка №${в.owner_id}`}
                </td>
                <td data-label="Раздел">{в.section_name ?? '—'}</td>
                <td data-label="Участников" className="num">
                  {в.is_meeting ? в.attendees : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ═══════════════════════════════ неделя ═══════════════════════════════
// Сетка людей по дням, как у календаря инженеров: колонок семь, на телефоне сетка
// прокручивается вбок, а не ломается в столбик из подписей.

function СеткаНедели({ д, пояс, выбрать }: {
  д: ОтветОкна
  пояс: string
  выбрать: (id: number) => void
}) {
  if (д.people.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="empty">
          <div className="empty-title">На этой неделе нет ни одной записи календаря</div>
          <div className="empty-hint">
            Это ответ про выбранное окно и фильтр, а не про весь календарь: полистайте
            недели или снимите выбор человека.
          </div>
        </div>
      </div>
    )
  }
  const поЛюдям = new Map<number, Вхождение[]>()
  for (const в of д.rows) {
    const свои = поЛюдям.get(в.owner_id) ?? []
    свои.push(в)
    поЛюдям.set(в.owner_id, свои)
  }
  return (
    <div className="card" style={{ marginBottom: 16, overflowX: 'auto' }}>
      <table style={{ minWidth: 900 }}>
        <thead>
          <tr>
            <th>Человек</th>
            {д.window.days.map((день) => (
              <th key={день.date}
                  className={день.weekend ? 'hint' : undefined}>
                {день.wd} {fmtDate(день.date)}{день.today ? ' · сегодня' : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {д.people.map((ч) => {
            const свои = поЛюдям.get(ч.owner_id) ?? []
            return (
              <tr key={ч.owner_id}>
                <td>
                  {/* Кнопка, а не ссылка: своей страницы у человека нет, нажатие сужает
                      календарь до него — тот же приём, что в календаре инженеров. */}
                  <button type="button" className="btn-link"
                          onClick={() => выбрать(ч.owner_id)}>
                    {ч.owner_name || `Учётка №${ч.owner_id}`}
                  </button>
                  <div className="hint">
                    {pluralN(ч.entries, 'запись', 'записи', 'записей')}
                    {ч.hidden > 0
                      ? `, из них ${pluralN(ч.hidden, 'частная', 'частные', 'частных')}`
                      : ''}
                  </div>
                </td>
                {д.window.days.map((день) => {
                  const вДень = свои.filter((в) => вДне(в, день.date, пояс))
                  return (
                    <td key={день.date}>
                      {вДень.map((в, i) => (
                        <div key={`${в.event_id}-${i}`} className="hint"
                             title={в.text_hidden
                               ? 'Частное событие: текст не переносился из Bitrix вовсе'
                               : подпись(в)}>
                          {в.all_day ? 'весь день' : времяВПоясе(в.starts_at, пояс)}
                          {' '}
                          {подпись(в)}
                        </div>
                      ))}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ═══════════════════════════════ границы данных ═══════════════════════════════

function Границы({ сп, оговорки }: {
  сп: Справочники | undefined
  оговорки: Record<string, string>
}) {
  return (
    <div className="card">
      <h2>Границы данных</h2>
      {оговорки.occurrences && <p className="hint">{оговорки.occurrences}</p>}
      {оговорки.copies && <p className="hint">{оговорки.copies}</p>}
      {оговорки.private && <p className="hint">{оговорки.private}</p>}
      {оговорки.as_is && <p className="hint">{оговорки.as_is}</p>}
      {оговорки.tz && <p className="hint">{оговорки.tz}</p>}
      {сп && (
        <p className="hint">
          Свежесть зеркала:{' '}
          {сп.freshness.length === 0
            ? 'записей о синке bxc:* нет — это само по себе повод насторожиться'
            : сп.freshness.map((з) =>
                `${з.entity} — ${з.last_sync_at ? fmtDate(з.last_sync_at, true) : 'ещё не было'}`)
              .join('; ')}
        </p>
      )}
    </div>
  )
}
